import { createContext, useContext, type ReactNode, useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import type { OrdemDoMenu, PermissionKey } from "@bookdental/schemas";
import { trpc } from "./trpc";
import { env } from "./env";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useMinimumLoading } from "./useMinimumLoading";
import { consumirMotivoDaRecarga, jaEstavaDentro, marcarSessaoIniciada } from "./sessao-visual";

type OrganizationType = "DENTAL_OFFICE" | "DENTAL_LAB" | "RADIOLOGY_CENTER";

export type SessionUser = {
  id: string;
  name: string | null;
  email: string;
  image: string | null;
  phone: string | null;
  croNumber: string | null;
  croState: string | null;
  croStatus: "PENDING" | "VERIFIED" | "REJECTED";
  platformRole: "MEMBER" | "ADMIN";
  onboardingCompleted: boolean;
  emailVerified: boolean;
  /** Falta aceitar a versão vigente dos termos e da privacidade (`versaoDosDocumentos`; null = não publicados). */
  aceiteDosDocumentosPendente: boolean;
  versaoDosDocumentos: string | null;
  /** Ordem do menu lateral que a pessoa arrastou. Vem junto da sessão de propósito — ver
   *  `me.current`. `null` = ordem do código. */
  sidebarOrder: OrdemDoMenu | null;
  /** Quadros da aba Início da ficha do paciente; null = padrão (ver use-quadros-da-ficha). */
  quadrosDaFicha: string[] | null;
  /** Aba aberta pelo suporte em "ver como" (só leitura) — ver lib/ver-como. */
  verComo: { adminNome: string | null; expiraEm: Date | string } | null;
  activeOrganization: {
    id: string;
    name: string;
    slug: string;
    types: OrganizationType[];
    isPersonal: boolean;
    /** Laboratório: quando montou o catálogo. Nulo = ainda vai pro "Montar catálogo". */
    catalogoMontadoEm: Date | string | null;
    /** Clínica: quando montou os procedimentos. Nulo = ainda vai pro "Montar procedimentos". */
    procedimentosMontadosEm: Date | string | null;
    /** Exclusão agendada pelo proprietário: até essa data a organização é só leitura. */
    exclusaoPrevistaPara: Date | string | null;
    plan: "FREE" | "PAID";
    role: string;
    canManageTeam: boolean;
    /** Mapa completo de permissões efetivas — base do gating item-a-item de menu (issue #10). */
    permissions: Record<PermissionKey, boolean>;
  } | null;
};

type AuthContextValue = {
  user: SessionUser | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Logout via pagina /logout da landing (same-origin com NextAuth). Evita CORS em prod
 * onde fetch cross-origin pra /api/auth/* do NextAuth seria bloqueado pelo browser.
 */
function signOut(): Promise<void> {
  window.location.href = `${env.landingUrl}/logout`;
  return Promise.resolve();
}

/** `ajustar`: transforma o usuário lido (ficha só leitura de outra clínica desliga as permissões
 *  de escrita sem mexer no servidor). */
export function AuthProvider({ children, ajustar }: { children: ReactNode; ajustar?: (u: SessionUser) => SessionUser }) {
  const meQuery = trpc.me.current.useQuery(undefined, {
    retry: false,
    staleTime: 60_000,
  });

  const user = useMemo(() => (meQuery.data && ajustar ? ajustar(meQuery.data) : (meQuery.data ?? null)), [meQuery.data, ajustar]);
  const value: AuthContextValue = {
    user,
    isLoading: meQuery.isLoading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  // Lido uma vez no primeiro render: depois que `marcarSessaoIniciada` roda, o valor mudaria no
  // meio da vida do componente e trocaria a tela de loading no ar.
  const [contexto] = useState(() => ({
    jaDentro: jaEstavaDentro(),
    motivo: consumirMotivoDaRecarga(),
  }));
  // 900ms mínimo só na chegada, pra animação de boas-vindas não flashar. Quem já está dentro não
  // deve esperar por enfeite: mostra o que precisa e sai da frente.
  const showLoading = useMinimumLoading(isLoading, contexto.jaDentro ? 0 : 900);

  useEffect(() => {
    if (user) marcarSessaoIniciada();
  }, [user]);

  if (showLoading) {
    return contexto.jaDentro ? (
      <LoadingScreen
        variant="interno"
        label={contexto.motivo === "troca-organizacao" ? "Trocando de organização" : "Carregando"}
      />
    ) : (
      <LoadingScreen label="Entrando na plataforma" />
    );
  }

  if (!user) {
    const callback = encodeURIComponent(window.location.origin + location.pathname);
    window.location.href = `${env.landingUrl}/login?callbackUrl=${callback}`;
    return null;
  }

  return <>{children}</>;
}
